import { useServiceTypeSettings, useServiceTypeTeams, type ServiceType } from '@church/data'
import { faClock, faCopy, faGear, faKey, faLink, faPlus, faUsers } from '@church/ui-icons'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { AccessListCard } from '../components/access/Access.tsx'
import { SectionPills } from '../components/SectionPills.tsx'
import {
  GeneralCard,
  GeneralPanel,
  TimesCard,
  TimesPanel,
} from '../components/services/ServiceTypeSettings.tsx'
import {
  AddExistingTeamPanel,
  NewTeamPanel,
  TeamsCard,
  useTeamPanels,
} from '../components/services/Teams.tsx'
import { NewTemplatePanel, TemplateRows } from '../components/services/Templates.tsx'
import {
  Button,
  Card,
  EmptyState,
  IconButton,
  ListCard,
  MOBILE_QUERY,
  PageHeader,
  ShownSection,
  Spinner,
  useMediaQuery,
} from '../components/ui.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'
import { folderCrumbs } from '../lib/services-crumbs.ts'
import { useTabParam } from '../lib/tabs.ts'

/**
 * A service type's settings (docs/14-TEAMS-PEOPLE-TEMPLATES.md, G3b), for
 * its Administrators: General, Times, Teams, Templates, Access. A computer
 * shows them all in two columns; phones and tablets one at a time behind
 * pills. Anyone else is told so, as the server would answer them: a
 * service type they cannot see is not there; one they can see but not
 * administer has settings that are not theirs.
 */
const SECTIONS = ['General', 'Times', 'Teams', 'Templates', 'Access'] as const
type Section = (typeof SECTIONS)[number]
const SECTION_ICONS = {
  General: faGear,
  Times: faClock,
  Teams: faUsers,
  Templates: faCopy,
  Access: faKey,
} as const

export const Route = createFileRoute('/_authed/_shell/services/types/$typeId/settings')({
  validateSearch: z.object({ tab: z.enum(SECTIONS).optional() }),
  component: ServiceTypeSettingsScreen,
})

function ServiceTypeSettingsScreen() {
  const { typeId } = Route.useParams()
  const settings = useServiceTypeSettings(typeId)
  const navigate = useNavigate()
  const mobile = useMediaQuery(MOBILE_QUERY)
  const [section, setSection] = useTabParam<Section>(SECTIONS)
  // Plans › Main campus › Sunday Morning › Settings.
  useBreadcrumbs([
    ...folderCrumbs(settings.folders),
    ...(settings.type ? [{ label: settings.type.name, href: `/services/types/${typeId}` }] : []),
    { label: 'Settings' },
  ])

  if (!settings.ready) return <Spinner />
  if (!settings.type)
    return (
      <Card>
        <EmptyState
          title="This service type isn’t here"
          hint="It may have been archived, or it isn’t one you can see."
          action={
            <Button variant="secondary" onClick={() => void navigate({ to: '/services/plans' })}>
              All plans
            </Button>
          }
        />
      </Card>
    )
  if (!settings.allowed)
    return (
      <Card>
        <EmptyState
          title="These settings aren’t yours"
          hint={`${settings.type.name}’s settings are for its Administrators.`}
          action={
            <Button
              variant="secondary"
              onClick={() => void navigate({ to: '/services/types/$typeId', params: { typeId } })}
            >
              Its plans
            </Button>
          }
        />
      </Card>
    )
  return (
    <SettingsSections
      type={settings.type}
      mobile={mobile}
      section={section}
      setSection={setSection}
    />
  )
}

function SettingsSections({
  type,
  mobile,
  section,
  setSection,
}: {
  type: ServiceType
  mobile: boolean
  section: Section
  setSection: (section: Section) => void
}) {
  const navigate = useNavigate()
  const teams = useServiceTypeTeams(type.id)
  const panels = useTeamPanels()
  const [open, setOpen] = useState<'general' | 'times' | 'existing' | 'template' | null>(null)
  const close = () => setOpen(null)
  const here = { id: type.id, name: type.name }

  const general = <GeneralCard type={type} onEdit={() => setOpen('general')} />
  const times = <TimesCard type={type} onEdit={() => setOpen('times')} />
  const teamsCard = (
    <TeamsCard
      title="Teams"
      rows={teams.serving}
      isPending={teams.isPending}
      error={teams.error}
      empty="No teams serve here yet"
      here={here}
      canRemove={teams.canRemove}
      panels={panels}
      actions={
        <>
          {teams.addable.length > 0 && (
            <Button variant="ghost" icon={faLink} onClick={() => setOpen('existing')}>
              Add existing
            </Button>
          )}
          <IconButton icon={faPlus} label="New team" onClick={panels.create} />
        </>
      }
    />
  )
  const templates = (
    <ListCard
      title="Templates"
      actions={
        <IconButton icon={faPlus} label="New template" onClick={() => setOpen('template')} />
      }
    >
      <TemplateRows typeId={type.id} />
    </ListCard>
  )
  const access = <AccessListCard scope={{ kind: 'service_type', id: type.id }} />

  return (
    <div className="space-y-6">
      <PageHeader title={`${type.name} settings`} />
      {mobile && (
        <SectionPills
          label="Settings sections"
          sections={SECTIONS}
          icons={SECTION_ICONS}
          value={section}
          onChange={setSection}
        />
      )}
      {mobile ? (
        <ShownSection value={section}>
          <div className="space-y-6">
            {section === 'General' && general}
            {section === 'Times' && times}
            {section === 'Teams' && teamsCard}
            {section === 'Templates' && templates}
            {section === 'Access' && access}
          </div>
        </ShownSection>
      ) : (
        <div className="grid grid-cols-plan items-start gap-6">
          <div className="min-w-0 space-y-6">
            {general}
            {times}
            {access}
          </div>
          <div className="min-w-0 space-y-6">
            {teamsCard}
            {templates}
          </div>
        </div>
      )}
      <GeneralPanel
        type={type}
        open={open === 'general'}
        onClose={close}
        // Archived: back to the level it sat in.
        onArchived={() =>
          void navigate({
            to: '/services/plans',
            search: type.folderId ? { folder: type.folderId } : {},
          })
        }
      />
      <TimesPanel type={type} open={open === 'times'} onClose={close} />
      <NewTeamPanel
        open={panels.creating}
        onClose={panels.closeCreate}
        serviceTypeId={type.id}
        onCreated={panels.open}
      />
      <AddExistingTeamPanel
        here={here}
        rows={teams.addable}
        open={open === 'existing'}
        onClose={close}
      />
      <NewTemplatePanel typeId={type.id} open={open === 'template'} onClose={close} />
    </div>
  )
}
